import React from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useResponsiveStore } from '@/stores/editor/responsive-store';
import { isContainerType } from '@/utils/componentTypes';
import { componentReferenceResolver } from '@/services/ai/ComponentReferenceResolver';
import { breakpointPropertyService } from '@/services/ai/BreakpointPropertyService';
import { responsivePositionMerger, type BreakpointName } from '@/services/ai/ResponsivePositionMerger';
import { commandStateManager } from '@/services/ai/CommandStateManager';
import { getBreakpointDisplayName } from '@/types/ai';
import { Component, ComponentType, Position } from '@/types';
import type { Size } from '@/types';
import type { ComponentUpdatePayload } from '@/types/component-update';
import type { PropertyMessage } from '@/components/ai/types';
import type { ComponentPropertyChanges } from './agentMessagingTypes';
import {
  applySchemaAwareStyleUpdate,
  getStylePropertyUpdateValue,
  hasSchemaAwareStyleProperty,
  hasV2ComponentStyleSlots,
} from '@/utils/component-style-normalization';
import { isColorSchemeWriteTarget } from '@/types/color-scheme';
import {
  getBlockedAICommandPermissionMessage,
  getMutationCategoriesForChanges,
  type AICommandPermissionGuard,
} from '@/services/ai/agentCommandPermissions';
import {
  copySanitizedOwnEnumerableProperties,
  isPlainRecord,
  mergeSanitizedRecords,
  omitSanitizedRecordKeys,
} from '@/utils/safeRecordSanitization';

export interface UsePropertyApplicationDeps {
  component: Component | null | undefined;
  multipleComponents?: Component[];
  isCanvas?: boolean;
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  updateComponent: (id: string, updates: Partial<ComponentUpdatePayload>, options?: { mergeBreakpoints?: boolean }) => void;
  resizeComponent: (id: string, size: Size) => void;
  updateCanvasBackground: (bg: Record<string, unknown>) => void;
  updateGridSettings: (settings: Record<string, unknown>) => void;
  updateCanvasLayout: (layout: Record<string, unknown>) => void;
  updateCanvasTheme: (theme: Record<string, unknown>) => void;
  updateResponsiveSettings: (settings: Record<string, unknown>) => void;
  commandPermissionGuard?: AICommandPermissionGuard;
}

export interface UsePropertyApplicationReturn {
  applyPropertyChanges: (messageId: string, changes: ComponentPropertyChanges) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isPlainRecord(value);
}

function isSizeDimension(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

function isBreakpointName(value: unknown): value is BreakpointName {
  return value === 'mobile' || value === 'tablet' || value === 'desktop';
}

function mergeComponentSize(
  baseSize: Size,
  sizeChanges: NonNullable<ComponentPropertyChanges['size']>
): Size {
  return {
    width: sizeChanges.width ?? baseSize.width,
    height: sizeChanges.height ?? baseSize.height,
  };
}

function completePosition(
  position: Partial<Position> | undefined,
  fallback: Position | undefined
): Position | undefined {
  if (!position) {
    return undefined;
  }

  const x = typeof position.x === 'number' ? position.x : fallback?.x;
  const y = typeof position.y === 'number' ? position.y : fallback?.y;
  const z = typeof position.z === 'number' ? position.z : fallback?.z ?? 0;

  return typeof x === 'number' && typeof y === 'number'
    ? { x, y, z }
    : undefined;
}

function isAutomationRuleArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function isMapPinRecord(value: unknown): value is Record<string, unknown> & { id: string } {
  return isRecord(value) && typeof value.id === 'string';
}

function copySafeOwnEnumerableProperties(
  target: Record<string, unknown>,
  source: unknown
): void {
  copySanitizedOwnEnumerableProperties(target, source);
}

function mergeSafeOwnEnumerableRecords(
  ...sources: unknown[]
): Record<string, unknown> {
  return mergeSanitizedRecords(...sources);
}

const omitRecordKeys = (source: Record<string, unknown>, keys: Iterable<string>): Record<string, unknown> => {
  return omitSanitizedRecordKeys(source, keys);
};

const normalizeFlatDimensions = (changes: ComponentPropertyChanges): ComponentPropertyChanges => {
  if (changes.size || (changes.width === undefined && changes.height === undefined)) {
    return changes;
  }

  const size: NonNullable<ComponentPropertyChanges['size']> = {};
  if (isSizeDimension(changes.width)) {
    size.width = changes.width;
  }
  if (isSizeDimension(changes.height)) {
    size.height = changes.height;
  }

  return {
    ...omitRecordKeys(changes, ['width', 'height']),
    size,
  };
};

const stripSizeDimensionsFromStyle = (changes: ComponentPropertyChanges): ComponentPropertyChanges => {
  if (!changes.size || !changes.style) {
    return changes;
  }

  const styleKeysToOmit: string[] = [];
  if (changes.size.width !== undefined && changes.style.width !== undefined) {
    styleKeysToOmit.push('width');
  }
  if (changes.size.height !== undefined && changes.style.height !== undefined) {
    styleKeysToOmit.push('height');
  }

  if (styleKeysToOmit.length === 0) {
    return changes;
  }

  return {
    ...changes,
    style: omitRecordKeys(changes.style, styleKeysToOmit),
  };
};

export function usePropertyApplication(deps: UsePropertyApplicationDeps): UsePropertyApplicationReturn {
  const {
    component,
    multipleComponents,
    isCanvas,
    setMessages,
    updateComponent,
    resizeComponent,
    updateCanvasBackground,
    updateGridSettings,
    updateCanvasLayout,
    updateCanvasTheme,
    updateResponsiveSettings,
    commandPermissionGuard,
  } = deps;

  const applyPropertyChanges = (messageId: string, changes: ComponentPropertyChanges) => {
    const toNumeric = (value: unknown): number | undefined => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : undefined;
      }

      return undefined;
    };

    const syncResponsiveProperties = (
      componentId: string,
      updates: { width?: number; height?: number; position?: Partial<Position> }
    ) => {
      const { activeViewport, setResponsiveProperty } = useResponsiveStore.getState();
      const viewportId = activeViewport || 'desktop';

      if (updates.width !== undefined) {
        setResponsiveProperty(componentId, 'size.width', viewportId, updates.width);
      }

      if (updates.height !== undefined) {
        setResponsiveProperty(componentId, 'size.height', viewportId, updates.height);
      }

      if (updates.position) {
        const { x, y, z } = updates.position;
        if (typeof x === 'number' && !Number.isNaN(x)) {
          setResponsiveProperty(componentId, 'position.x', viewportId, x);
        }
        if (typeof y === 'number' && !Number.isNaN(y)) {
          setResponsiveProperty(componentId, 'position.y', viewportId, y);
        }
        if (typeof z === 'number' && !Number.isNaN(z)) {
          setResponsiveProperty(componentId, 'position.z', viewportId, z);
        }
      }
    };

    // CRITICAL: Always get fresh component data from store to avoid stale prop values
    // This ensures we have the latest responsivePosition after previous modifications
    const { components: storeComponents, project: activeProject } = useEditorStore.getState();
    const activeSchemaVersion = activeProject?.schemaVersion;
    const storeStyleWriteTarget = useEditorStore.getState().styleWriteTarget;
    const activeStyleWriteTarget = isColorSchemeWriteTarget(changes.styleWriteTarget)
      ? changes.styleWriteTarget
      : storeStyleWriteTarget;
    const shouldMirrorTextStyleToProperties = (target: Component): boolean =>
      activeSchemaVersion !== 2 && !hasV2ComponentStyleSlots(target);

    // Check if we need to resolve a component reference
    let targetComponent = component ? storeComponents.find(c => c.id === component.id) || component : component;

    // If there's a specific target component ID, use that
    if (changes.targetComponentId) {
      targetComponent = storeComponents.find(c => c.id === changes.targetComponentId) || component;
    }
    // If we're in canvas mode and there's a component reference, resolve it
    else if (isCanvas && changes.componentReference) {
      const resolved = componentReferenceResolver.resolveReference(changes.componentReference, {
        selectedComponents: multipleComponents || (component ? [component] : [])
      });

      if (resolved) {
        targetComponent = resolved.component;
      }
    }

    const permissionCategories = getMutationCategoriesForChanges(changes, {
      isCanvasMutation: isCanvas && !targetComponent,
    });
    const permissionTargets = targetComponent
      ? [targetComponent]
      : multipleComponents;
    const permissionResult = commandPermissionGuard?.({
      categories: permissionCategories,
      project: activeProject,
      targetComponents: permissionTargets,
      isCanvasMutation: isCanvas && !targetComponent,
    });

    if (permissionResult && !permissionResult.allowed) {
      const blockedContent = getBlockedAICommandPermissionMessage(permissionResult, 'content');
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, content: blockedContent, isStreaming: false, applied: false }
            : msg
        )
      );
      return;
    }

    if (isCanvas && !targetComponent) {
      // Apply canvas changes
      if (changes.background) {
        updateCanvasBackground(changes.background);
      }
      if (changes.grid) {
        updateGridSettings(changes.grid);
      }
      if (changes.layout) {
        updateCanvasLayout(changes.layout);
      }
      if (changes.theme) {
        updateCanvasTheme(changes.theme);
      }
      if (changes.responsive) {
        updateResponsiveSettings(changes.responsive);
      }

      // Mark as applied
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, applied: true } : msg
        )
      );
    } else if (targetComponent) {
      // ============================================================================
      // Phase 3.1: Handle Breakpoint-Specific Changes
      // ============================================================================
      const shouldApplyV2StyleBreakpointLocally =
        changes.style !== undefined &&
        (activeSchemaVersion === 2 || hasV2ComponentStyleSlots(targetComponent));

      if (breakpointPropertyService.isBreakpointSpecific(changes) && !shouldApplyV2StyleBreakpointLocally) {
        console.log('[Phase 3.1 applyPropertyChanges] Breakpoint-specific changes detected:', changes);

        let result;
        if (changes.responsiveStyles) {
          // Multiple breakpoints specified (e.g., "stack on mobile, horizontal on desktop")
          result = breakpointPropertyService.applyResponsiveStyles(
            targetComponent,
            changes.responsiveStyles
          );
        } else if (changes.breakpoint) {
          // Single breakpoint specified (e.g., "hide on mobile")
          result = breakpointPropertyService.applyToBreakpoint(
            targetComponent,
            changes,
            changes.breakpoint
          );
        }

        if (result && result.success && result.componentUpdate) {
          console.log('[Phase 3.1 applyPropertyChanges] Applying breakpoint update:', result);

          // Apply the responsivePosition update
          updateComponent(targetComponent.id, result.componentUpdate);

          // Log affected breakpoints for user feedback
          const breakpointNames = result.affectedBreakpoints
            .map(bp => getBreakpointDisplayName(bp))
            .join(', ');

          console.log(`[Phase 3.1 applyPropertyChanges] Applied changes to breakpoints: ${breakpointNames}`);

          // Mark as applied
          setMessages(prev =>
            prev.map(msg =>
              msg.id === messageId ? { ...msg, applied: true } : msg
            )
          );

          // Return early - breakpoint changes are complete
          return;
        } else if (result) {
          console.warn('[Phase 3.1 applyPropertyChanges] Breakpoint application failed:', result);
        }
      }
      // ============================================================================
      // End Phase 3.1 Breakpoint Handling
      // ============================================================================

      const responsiveUpdates: { width?: number; height?: number; position?: Partial<Position> } = {};

      // Handle width/height from flat changes (from slash commands) by converting to size
      changes = normalizeFlatDimensions(changes);

      // SAFEGUARD: Strip width/height from style when they're in size
      // Components fill their wrapper, so size controls dimensions, not style
      changes = stripSizeDimensionsFromStyle(changes);

      const updateData: Partial<ComponentUpdatePayload> = {};

      // Apply to single component
      if (changes.size) {
        // CRITICAL FIX: For flex containers, cards, charts, and tables, ONLY update the responsive breakpoint style,
        // NOT the component.size. The wrapper uses responsive breakpoint styles for width/height,
        // and updating component.size causes the inner component to have a different size than the wrapper.
        // PHASE 5.4 FIX: Charts were not included, causing "w 200" to update size but not style.width,
        // leaving the wrapper at 100% width (the "phantom full-width container" bug).
        const isFlexChildComponent = isContainerType(targetComponent.type) ||
                            // Charts - use responsive breakpoint style for proper wrapper sizing
                            targetComponent.type === ComponentType.LINE_CHART ||
                            targetComponent.type === ComponentType.BAR_CHART ||
                            targetComponent.type === ComponentType.PIE_CHART ||
                            targetComponent.type === ComponentType.AREA_CHART ||
                            targetComponent.type === ComponentType.SCATTER_CHART ||
                            targetComponent.type === ComponentType.HEATMAP_CHART ||
                            targetComponent.type === ComponentType.TREEMAP_CHART ||
                            targetComponent.type === ComponentType.GAUGE_CHART ||
                            targetComponent.type === ComponentType.MAP_CHART ||
                            // Tables
                            targetComponent.type === ComponentType.TABLE ||
                            // Kanban boards
                            targetComponent.type === ComponentType.KANBAN_BOARD ||
                            // Timelines
                            targetComponent.type === ComponentType.HORIZONTAL_TIMELINE;

        if (isFlexChildComponent && (changes.size.width !== undefined || changes.size.height !== undefined)) {

          // ============================================================================
          // Phase 5.4: Detect target breakpoint from changes or command context
          // If no breakpoint specified, apply to ALL breakpoints (universal sizing)
          // ============================================================================
          const targetBreakpoint = isBreakpointName(changes.breakpoint)
            ? changes.breakpoint
            : undefined;
          const hasExplicitBreakpoint = targetBreakpoint !== undefined;

          // ============================================================================
          // Phase 5.4: Use ResponsivePositionMerger for deep merge
          // This ensures we don't lose mobile/tablet styles when updating desktop
          // ============================================================================
          const sizeChanges = {
            size: {
              ...(changes.size.width !== undefined ? { width: changes.size.width } : {}),
              ...(changes.size.height !== undefined ? { height: changes.size.height } : {})
            }
          };

          let mergeResult;

          if (hasExplicitBreakpoint) {
            // Breakpoint-specific command (e.g., "tablet: w 200") - apply only to that breakpoint
            console.log('[Phase 5.4] Using explicit breakpoint from changes:', targetBreakpoint);

            // Validate the merge before applying
            const validation = responsivePositionMerger.validateMerge(
              targetComponent.responsivePosition,
              sizeChanges,
              targetBreakpoint
            );

            if (validation.warnings.length > 0) {
              console.log('[Phase 5.4] Merge warnings:', validation.warnings);
            }

            // Perform the deep merge to single breakpoint
            mergeResult = responsivePositionMerger.mergeChanges(
              targetComponent.responsivePosition,
              sizeChanges,
              targetBreakpoint
            );
          } else {
            // Universal command (e.g., "w 200") - apply to ALL breakpoints
            console.log('[Phase 5.4] No explicit breakpoint - applying to ALL breakpoints');

            // Use mergeIntoMultipleBreakpoints to apply to all three breakpoints
            mergeResult = responsivePositionMerger.mergeIntoMultipleBreakpoints(
              targetComponent.responsivePosition,
              sizeChanges,
              ['mobile', 'tablet', 'desktop']
            );
          }

          // Log the change summary for debugging
          const changeSummary = responsivePositionMerger.getChangeSummary(
            targetComponent.responsivePosition,
            mergeResult.mergedPosition
          );

          console.log('[Phase 5.4] Applying merged changes:', {
            componentId: targetComponent.id,
            isUniversal: !hasExplicitBreakpoint,
            affectedBreakpoints: mergeResult.affectedBreakpoints,
            hasChanges: mergeResult.hasChanges,
            summary: changeSummary
          });

          // Add to updateData so it gets applied with the component update
          updateData.responsivePosition = mergeResult.mergedPosition;
        } else {
          // For non-flex/card components, use the traditional resizeComponent approach
          resizeComponent(targetComponent.id, mergeComponentSize(targetComponent.size, changes.size));

          // Only sync responsive properties that were actually changed (not the merged ones)
          const widthValue = toNumeric(changes.size.width);
          if (widthValue !== undefined) {
            responsiveUpdates.width = widthValue;
          }

          const heightValue = toNumeric(changes.size.height);
          if (heightValue !== undefined) {
            responsiveUpdates.height = heightValue;
          }
        }
      }

      // Handle chart type conversion
      if (changes.type) {
        updateData.type = changes.type;
      }

      // Aggregate property updates from various sources
      const propertyUpdates: Record<string, unknown> = {};

      if (changes.properties && typeof changes.properties === 'object') {
        copySafeOwnEnumerableProperties(propertyUpdates, changes.properties);
      }

      const directTableKeys = [
        'columns',
        'data',
        'queryBinding',
        'autoRefresh',
        'stats',
        'tenantId',
        'queryId',
      ];

      directTableKeys.forEach(key => {
        if (key in changes) {
          propertyUpdates[key] = changes[key];
        }
      });

      // Check if there are direct property changes (from quick patterns)
      const propertyKeys = ['content', 'text', 'fontSize', 'fontWeight', 'fontStyle',
                          'textAlign', 'lineHeight', 'letterSpacing', 'textTransform',
                          'textDecoration', 'fontFamily', 'variant', 'title', 'icon',
                          'disabled', 'loading', 'fullWidth', 'placeholder', 'inputType',
                          // Hover and shadow properties
                          'hoverBackgroundColor', 'hoverColor', 'shadowBlur', 'shadowSpread',
                          'shadowOffsetX', 'shadowOffsetY',
                          // Icon component properties
                          'iconName', 'library', 'strokeWidth', 'rotation', 'fill', 'label',
                          // Chart properties
                          'xAxisKey', 'yAxisKey', 'lineKeys', 'lineLabels', 'barKeys', 'barLabels', 'areaKeys', 'areaLabels',
                          'seriesKeys', 'seriesLabels', 'nameKey', 'valueKey', 'sizeKey', 'categoryKey', 'chartType',
                          'cubeQuery', 'showLegend', 'showTooltip', 'showGrid', 'colors', 'responsive',
                          'animation', 'margin', 'showXAxis', 'showYAxis', 'xAxisLabel', 'yAxisLabel',
                          'xDomain', 'yDomain', 'showDots', 'showLabels', 'outerRadius',
                          'innerRadius', 'needleColor', 'value', 'max', 'min',
                          // Map properties
                          'mapStyle', 'tileProvider'];

      propertyKeys.forEach(key => {
        if (key in changes) {
          propertyUpdates[key] = changes[key];
        }
      });

      // Phase 2: Handle responsiveChartConfig for chart-specific breakpoint settings
      // This allows commands like "on mobile hide legend" to set per-breakpoint chart config
      if (changes.responsiveChartConfig) {
        const existingConfig = isRecord(targetComponent.properties?.responsiveChartConfig)
          ? targetComponent.properties.responsiveChartConfig
          : {};
        const newConfig = changes.responsiveChartConfig;
        const mergedConfig: Record<string, unknown> = {};

        // Deep merge each breakpoint to preserve existing settings
        for (const bp of ['mobile', 'tablet', 'desktop']) {
          const existingBp = isRecord(existingConfig[bp]) ? existingConfig[bp] : undefined;
          const newBp = isRecord(newConfig[bp]) ? newConfig[bp] : undefined;
          if (existingBp || newBp) {
            mergedConfig[bp] = mergeSafeOwnEnumerableRecords(existingBp, newBp);
          }
        }

        propertyUpdates.responsiveChartConfig = mergedConfig;
        console.log('[Phase 2] Merged responsiveChartConfig:', mergedConfig);
      }

      // ── Automation Rules: resolve _automationRuleToAdd / _automationRuleListDescription ──
      let resolvedPropertyUpdates = propertyUpdates;
      if (resolvedPropertyUpdates._automationRuleToAdd) {
        const existingRules = isAutomationRuleArray(targetComponent.properties?.automationRules)
          ? targetComponent.properties.automationRules
          : [];
        resolvedPropertyUpdates = {
          ...omitRecordKeys(resolvedPropertyUpdates, ['_automationRuleToAdd']),
          automationRules: [...existingRules, resolvedPropertyUpdates._automationRuleToAdd],
        };
      }
      if (resolvedPropertyUpdates._automationRuleListDescription) {
        resolvedPropertyUpdates = omitRecordKeys(resolvedPropertyUpdates, ['_automationRuleListDescription']);
      }

      if (Object.keys(resolvedPropertyUpdates).length > 0) {
        // Deep merge nested objects like initialView and layers for map components
        // This prevents losing properties like 'center' when only 'zoom' is updated
        const existingProps = targetComponent.properties || {};
        const mergedProperties = mergeSafeOwnEnumerableRecords(existingProps, resolvedPropertyUpdates);

        // Deep merge initialView to preserve center/pitch/bearing when only zoom changes
        if (isRecord(resolvedPropertyUpdates.initialView) && isRecord(existingProps.initialView)) {
          mergedProperties.initialView = mergeSafeOwnEnumerableRecords(
            existingProps.initialView,
            resolvedPropertyUpdates.initialView
          );
        }

        // Deep merge layers to preserve layer configs when only one is updated
        if (isRecord(resolvedPropertyUpdates.layers) && isRecord(existingProps.layers)) {
          mergedProperties.layers = mergeSafeOwnEnumerableRecords(
            existingProps.layers,
            resolvedPropertyUpdates.layers
          );
        }

        // Deep merge pins array by ID to preserve lat/lng/style when only icon changes
        if (resolvedPropertyUpdates.pins && Array.isArray(resolvedPropertyUpdates.pins) && existingProps.pins && Array.isArray(existingProps.pins)) {
          const existingPinsMap = new Map(
            existingProps.pins
              .filter(isMapPinRecord)
              .map((pin) => [pin.id, pin])
          );
          mergedProperties.pins = resolvedPropertyUpdates.pins.map((partialPin) => {
            if (!isMapPinRecord(partialPin)) {
              return partialPin;
            }
            const existing = existingPinsMap.get(partialPin.id);
            if (existing) {
              const mergedPin = mergeSafeOwnEnumerableRecords(existing, partialPin);
              const mergedStyle = mergeSafeOwnEnumerableRecords(
                isRecord(existing.style) ? existing.style : undefined,
                isRecord(partialPin.style) ? partialPin.style : undefined
              );
              const mergedNavigation = partialPin.navigation !== undefined
                ? mergeSafeOwnEnumerableRecords(
                  isRecord(existing.navigation) ? existing.navigation : undefined,
                  isRecord(partialPin.navigation) ? partialPin.navigation : undefined
                )
                : existing.navigation;

              return {
                ...mergedPin,
                style: mergedStyle,
                navigation: mergedNavigation,
              };
            }
            return mergeSafeOwnEnumerableRecords(partialPin);
          });
        }

        updateData.properties = mergedProperties;
      }

      // Handle style properties separately (from quick patterns or regular changes)
      // NOTE: width and height should NOT be in styleKeys - they belong to size property
      const styleKeys = ['color', 'backgroundColor', 'opacity', 'display', 'border',
                        'borderRadius', 'boxShadow', 'textShadow', 'padding', 'margin',
                        'position', 'top', 'left', 'right', 'bottom',
                        'zIndex', 'transform', 'cursor', 'overflow', 'gap', 'flexDirection',
                        'justifyContent', 'alignItems'];

      const directStyles: Record<string, unknown> = {};
      styleKeys.forEach(key => {
        if (key in changes && !changes.style?.[key]) {
          directStyles[key] = changes[key];
        }
      });

      if (changes.style || Object.keys(directStyles).length > 0) {
          updateData.style = applySchemaAwareStyleUpdate(
            targetComponent,
          mergeSafeOwnEnumerableRecords(
            isRecord(changes.style) ? changes.style : undefined,
            directStyles
          ),
          {
            projectSchemaVersion: activeSchemaVersion,
            breakpoint: changes.breakpoint,
            styleWriteTarget: activeStyleWriteTarget,
          },
        );
      }

      // v1 text rendering still checks properties color values, so mirror style edits for
      // old flat components only. v2 keeps CSS values inside schema style slots.
      // NOTE: We only set the specific color properties, NOT spreading all existing properties
      // to avoid accidentally copying content or other unrelated properties during style cloning.
      const isTextComponent = targetComponent.type === ComponentType.TEXT;
      if (isTextComponent && shouldMirrorTextStyleToProperties(targetComponent)) {
        const styleColor = getStylePropertyUpdateValue(changes.style, 'color', changes.breakpoint);
        const resolvedColor = styleColor || directStyles.color;
        if (resolvedColor) {
          if (!updateData.properties) {
            updateData.properties = {};
          }
          updateData.properties.color = resolvedColor;
        }
        // Also handle backgroundColor for text components
        const styleBackgroundColor = getStylePropertyUpdateValue(changes.style, 'backgroundColor', changes.breakpoint);
        const newBgColor = styleBackgroundColor || directStyles.backgroundColor;
        if (newBgColor) {
          if (!updateData.properties) {
            updateData.properties = {};
          }
          updateData.properties.backgroundColor = newBgColor;
        }
      }

      // CRITICAL: When applying size changes, remove width/height/minHeight from style
      // Components should fill their wrapper, so size controls dimensions, not style
      // PHASE 5 FIX: Also remove minHeight when changing height - CSS minHeight overrides height,
      // causing the "wrapper stays at old size" bug for charts and other components
      if (changes.size) {
        // ROBUST FIX: When setting size dimensions, clear style dimensions through
        // the same schema-aware path used for style writes.
        const needsWidthCleanup = changes.size.width !== undefined &&
          (hasSchemaAwareStyleProperty(targetComponent, 'width', { projectSchemaVersion: activeSchemaVersion }) ||
            hasSchemaAwareStyleProperty(targetComponent, 'minWidth', { projectSchemaVersion: activeSchemaVersion }));
        const needsHeightCleanup = changes.size.height !== undefined &&
          (hasSchemaAwareStyleProperty(targetComponent, 'height', { projectSchemaVersion: activeSchemaVersion }) ||
            hasSchemaAwareStyleProperty(targetComponent, 'minHeight', { projectSchemaVersion: activeSchemaVersion }));

        if (needsWidthCleanup || needsHeightCleanup) {
          const dimensionCleanup: Record<string, unknown> = {};
          if (changes.size.width !== undefined) {
            dimensionCleanup.width = undefined;
            dimensionCleanup.minWidth = undefined;
            console.log('[Phase 5 Fix] Clearing style.width and style.minWidth for responsive size update');
          }
          if (changes.size.height !== undefined) {
            dimensionCleanup.height = undefined;
            dimensionCleanup.minHeight = undefined;
            console.log('[Phase 5 Fix] Clearing style.height and style.minHeight for responsive size update');
          }

          updateData.style = applySchemaAwareStyleUpdate(
            { style: updateData.style ?? targetComponent.style },
            dimensionCleanup,
            {
              projectSchemaVersion: activeSchemaVersion,
              breakpoint: changes.breakpoint,
              styleWriteTarget: activeStyleWriteTarget,
            },
          );
        }
      }

      const positionUpdate = completePosition(changes.position, targetComponent.position);
      if (positionUpdate) {
        updateData.position = positionUpdate;
      }

      if (responsiveUpdates.width === undefined) {
        const widthCandidate = toNumeric(
          getStylePropertyUpdateValue(changes.style, 'width', changes.breakpoint) !== undefined
            ? getStylePropertyUpdateValue(changes.style, 'width', changes.breakpoint)
            : directStyles.width
        );
        if (widthCandidate !== undefined) {
          responsiveUpdates.width = widthCandidate;
        }
      }

      if (responsiveUpdates.height === undefined) {
        const heightCandidate = toNumeric(
          getStylePropertyUpdateValue(changes.style, 'height', changes.breakpoint) !== undefined
            ? getStylePropertyUpdateValue(changes.style, 'height', changes.breakpoint)
            : directStyles.height
        );
        if (heightCandidate !== undefined) {
          responsiveUpdates.height = heightCandidate;
        }
      }

      if (updateData.position) {
        responsiveUpdates.position = updateData.position;
      } else if (changes.position) {
        responsiveUpdates.position = changes.position;
      }

      // Special handling for container height (flex-container, container, card)
      const changedStyleHeight = getStylePropertyUpdateValue(changes.style, 'height', changes.breakpoint);
      if (isContainerType(targetComponent.type) && changedStyleHeight) {
        // For flex-containers, height with vh/% units should be set as minHeight in properties
        const heightValue = changedStyleHeight;
        if (typeof heightValue === 'string' && (heightValue.includes('vh') || heightValue.includes('%'))) {
          if (!updateData.properties) {
            updateData.properties = { ...targetComponent.properties };
          }
          updateData.properties.minHeight = heightValue;
        }
      }

      if (Object.keys(updateData).length > 0) {
        updateComponent(targetComponent.id, updateData);

        // ============================================================================
        // PHASE 5 FIX: Update parent container's minHeight when child is resized
        // Auto-containers set a fixed minHeight (e.g., 432px) that doesn't shrink when children shrink
        // We need to clear/update the parent's minHeight to allow the container to resize
        // ============================================================================
        if (changes.size?.height !== undefined) {
          const { components: allComponents } = useEditorStore.getState();

          // Find parent container - check both children array and parentId
          const parent = allComponents.find(c =>
            // Parent has this component in its children array
            c.children?.includes(targetComponent.id) ||
            // Or this component has a parentId pointing to a flex-container
            (c.id === targetComponent.parentId && isContainerType(c.type))
          );

          if (parent && isContainerType(parent.type)) {
            // Check if parent has a fixed minHeight that's larger than the new content
            // minHeight can be in style or responsivePosition
            const parentMinHeight = parent.style?.minHeight ||
              parent.responsivePosition?.breakpoints?.desktop?.style?.minHeight;
            const newChildHeight = typeof changes.size.height === 'number'
              ? changes.size.height
              : parseInt(String(changes.size.height), 10);

            if (parentMinHeight && typeof newChildHeight === 'number' && !isNaN(newChildHeight)) {
              const parentMinHeightPx = typeof parentMinHeight === 'number'
                ? parentMinHeight
                : parseInt(String(parentMinHeight), 10);

              // If parent's minHeight is larger than new content + padding, update it
              const padding = 32; // Default padding for auto-containers
              const newMinHeight = newChildHeight + padding;

              if (parentMinHeightPx > newMinHeight) {
                console.log('[Phase 5 Fix] Updating parent container minHeight:', {
                  parentId: parent.id,
                  oldMinHeight: parentMinHeightPx,
                  newMinHeight,
                  childHeight: newChildHeight
                });

                // Clear the parent's fixed minHeight to let it size to content
                const parentStyleUpdate = omitRecordKeys(parent.style ?? {}, ['minHeight']);

                // Also clear from responsivePosition if present
                const parentUpdate: Partial<ComponentUpdatePayload> = { style: parentStyleUpdate };
                if (parent.responsivePosition?.breakpoints?.desktop?.style?.minHeight) {
                  const desktopBreakpoint = parent.responsivePosition.breakpoints.desktop;
                  parentUpdate.responsivePosition = {
                    ...parent.responsivePosition,
                    breakpoints: {
                      ...parent.responsivePosition.breakpoints,
                      desktop: {
                        ...desktopBreakpoint,
                        style: omitRecordKeys(desktopBreakpoint.style ?? {}, ['minHeight']),
                      },
                    },
                  };
                }

                updateComponent(parent.id, parentUpdate);
              }
            }
          }
        }

        // ============================================================================
        // Phase 5.1: State Verification and Cache Update
        // Verify changes were applied and update the cached state for sequential commands
        // ============================================================================
        // Use setTimeout to ensure React state has propagated
        setTimeout(() => {
          const freshState = useEditorStore.getState();
          const updatedComponent = freshState.components.find(c => c.id === targetComponent.id);

          if (updatedComponent) {
            // Capture the new state for future commands
            commandStateManager.captureState(updatedComponent);
          }
        }, 50); // Small delay to allow React state to propagate

      }

      if (
        responsiveUpdates.width !== undefined ||
        responsiveUpdates.height !== undefined ||
        responsiveUpdates.position
      ) {
        syncResponsiveProperties(targetComponent.id, responsiveUpdates);
      }

    } else if (multipleComponents) {
      // Handle width/height from flat changes (from slash commands) by converting to size
      changes = normalizeFlatDimensions(changes);

      // SAFEGUARD: Strip width/height from style when they're in size
      // Components fill their wrapper, so size controls dimensions, not style
      changes = stripSizeDimensionsFromStyle(changes);

      // Apply to multiple components
      multipleComponents.forEach(comp => {
        const responsiveUpdates: { width?: number; height?: number; position?: Partial<Position> } = {};
        const updateData: Partial<ComponentUpdatePayload> = {};

        if (changes.size) {
          // PHASE 5.4 FIX: Check if component needs responsive position merger (same logic as single component)
          const isFlexChildComponent = isContainerType(comp.type) ||
                              // Charts - use responsive breakpoint style for proper wrapper sizing
                              comp.type === ComponentType.LINE_CHART ||
                              comp.type === ComponentType.BAR_CHART ||
                              comp.type === ComponentType.PIE_CHART ||
                              comp.type === ComponentType.AREA_CHART ||
                              comp.type === ComponentType.SCATTER_CHART ||
                              comp.type === ComponentType.HEATMAP_CHART ||
                              comp.type === ComponentType.TREEMAP_CHART ||
                              comp.type === ComponentType.GAUGE_CHART ||
                              comp.type === ComponentType.MAP_CHART ||
                              // Tables
                              comp.type === ComponentType.TABLE ||
                              // Kanban boards
                              comp.type === ComponentType.KANBAN_BOARD ||
                              // Timelines
                              comp.type === ComponentType.HORIZONTAL_TIMELINE;

          if (isFlexChildComponent && (changes.size.width !== undefined || changes.size.height !== undefined)) {
            // Use ResponsivePositionMerger for flex child components
            const targetBreakpoint = isBreakpointName(changes.breakpoint)
              ? changes.breakpoint
              : undefined;
            const hasExplicitBreakpoint = targetBreakpoint !== undefined;

            const sizeChanges = {
              size: {
                ...(changes.size.width !== undefined ? { width: changes.size.width } : {}),
                ...(changes.size.height !== undefined ? { height: changes.size.height } : {})
              }
            };

            let mergeResult;
            if (hasExplicitBreakpoint) {
              // Breakpoint-specific - apply only to that breakpoint
              mergeResult = responsivePositionMerger.mergeChanges(
                comp.responsivePosition,
                sizeChanges,
                targetBreakpoint
              );
            } else {
              // Universal - apply to ALL breakpoints
              mergeResult = responsivePositionMerger.mergeIntoMultipleBreakpoints(
                comp.responsivePosition,
                sizeChanges,
                ['mobile', 'tablet', 'desktop']
              );
            }

            console.log('[Phase 5.4] Multi-select: Applying merged changes to', comp.id, {
              isUniversal: !hasExplicitBreakpoint,
              affectedBreakpoints: mergeResult.affectedBreakpoints,
              hasChanges: mergeResult.hasChanges
            });

            updateData.responsivePosition = mergeResult.mergedPosition;
          } else {
            // For non-flex-child components, use the traditional resizeComponent approach
            resizeComponent(comp.id, mergeComponentSize(comp.size, changes.size));

            // Only sync responsive properties that were actually changed (not the merged ones)
            const widthValue = toNumeric(changes.size.width);
            if (widthValue !== undefined) {
              responsiveUpdates.width = widthValue;
            }

            const heightValue = toNumeric(changes.size.height);
            if (heightValue !== undefined) {
              responsiveUpdates.height = heightValue;
            }
          }
        }

        // Handle chart type conversion
        if (changes.type) {
          updateData.type = changes.type;
        }

        const propertyUpdates: Record<string, unknown> = {};

        if (changes.properties && typeof changes.properties === 'object') {
          copySafeOwnEnumerableProperties(propertyUpdates, changes.properties);
        }

        const directTableKeys = [
          'columns',
          'data',
          'queryBinding',
          'autoRefresh',
          'stats',
          'tenantId',
          'queryId',
        ];

        directTableKeys.forEach(key => {
          if (key in changes) {
            propertyUpdates[key] = changes[key];
          }
        });

        const propertyKeys = ['content', 'text', 'fontSize', 'fontWeight', 'fontStyle',
                            'textAlign', 'lineHeight', 'letterSpacing', 'textTransform',
                            'textDecoration', 'fontFamily', 'variant', 'title', 'icon',
                            'disabled', 'loading', 'fullWidth', 'placeholder', 'inputType',
                            // Chart properties
                            'xAxisKey', 'yAxisKey', 'lineKeys', 'lineLabels', 'barKeys', 'barLabels', 'areaKeys', 'areaLabels',
                            'seriesKeys', 'seriesLabels', 'nameKey', 'valueKey', 'sizeKey', 'categoryKey', 'chartType',
                            'cubeQuery', 'showLegend', 'showTooltip', 'showGrid', 'colors', 'responsive',
                            'animation', 'margin', 'showXAxis', 'showYAxis', 'xAxisLabel', 'yAxisLabel',
                            'xDomain', 'yDomain', 'showDots', 'showLabels', 'outerRadius',
                            'innerRadius', 'needleColor', 'value', 'max', 'min',
                            // Map properties
                            'mapStyle', 'tileProvider'];

        propertyKeys.forEach(key => {
          if (key in changes) {
            propertyUpdates[key] = changes[key];
          }
        });

        // Phase 2: Handle responsiveChartConfig for chart-specific breakpoint settings
        if (changes.responsiveChartConfig) {
          const existingConfig = isRecord(comp.properties?.responsiveChartConfig)
            ? comp.properties.responsiveChartConfig
            : {};
          const newConfig = changes.responsiveChartConfig;
          const mergedConfig: Record<string, unknown> = {};

          // Deep merge each breakpoint to preserve existing settings
          for (const bp of ['mobile', 'tablet', 'desktop']) {
            const existingBp = isRecord(existingConfig[bp]) ? existingConfig[bp] : undefined;
            const newBp = isRecord(newConfig[bp]) ? newConfig[bp] : undefined;
            if (existingBp || newBp) {
              mergedConfig[bp] = mergeSafeOwnEnumerableRecords(existingBp, newBp);
            }
          }

          propertyUpdates.responsiveChartConfig = mergedConfig;
        }

        if (Object.keys(propertyUpdates).length > 0) {
          // Deep merge nested objects like initialView and layers for map components
          const existingProps = comp.properties || {};
          const mergedProperties = mergeSafeOwnEnumerableRecords(existingProps, propertyUpdates);

          // Deep merge initialView to preserve center/pitch/bearing when only zoom changes
          if (isRecord(propertyUpdates.initialView) && isRecord(existingProps.initialView)) {
            mergedProperties.initialView = mergeSafeOwnEnumerableRecords(
              existingProps.initialView,
              propertyUpdates.initialView
            );
          }

          // Deep merge layers to preserve layer configs
          if (isRecord(propertyUpdates.layers) && isRecord(existingProps.layers)) {
            mergedProperties.layers = mergeSafeOwnEnumerableRecords(
              existingProps.layers,
              propertyUpdates.layers
            );
          }

          updateData.properties = mergedProperties;
        }

        // Handle style properties separately (from quick patterns or regular changes)
        // NOTE: width and height should NOT be in styleKeys - they belong to size property
        const styleKeys = ['color', 'backgroundColor', 'opacity', 'display', 'border',
                          'borderRadius', 'boxShadow', 'textShadow', 'padding', 'margin',
                          'position', 'top', 'left', 'right', 'bottom',
                          'zIndex', 'transform', 'cursor', 'overflow', 'gap', 'flexDirection',
                          'justifyContent', 'alignItems'];

        const directStyles: Record<string, unknown> = {};
        styleKeys.forEach(key => {
          if (key in changes && !changes.style?.[key]) {
            directStyles[key] = changes[key];
          }
        });

        if (changes.style || Object.keys(directStyles).length > 0) {
          updateData.style = applySchemaAwareStyleUpdate(
            comp,
            mergeSafeOwnEnumerableRecords(
              isRecord(changes.style) ? changes.style : undefined,
              directStyles
            ),
            {
              projectSchemaVersion: activeSchemaVersion,
              breakpoint: changes.breakpoint,
              styleWriteTarget: activeStyleWriteTarget,
            },
          );
        }

        // v1 text rendering still checks properties color values, so mirror style edits for
        // old flat components only. v2 keeps CSS values inside schema style slots.
        const isTextComp = comp.type === ComponentType.TEXT;
        if (isTextComp && shouldMirrorTextStyleToProperties(comp)) {
          const styleColor = getStylePropertyUpdateValue(changes.style, 'color', changes.breakpoint);
          const newColor = styleColor || directStyles.color;
          if (newColor) {
            if (!updateData.properties) {
              updateData.properties = { ...comp.properties };
            }
            updateData.properties.color = newColor;
          }
          // Also handle backgroundColor for text components
          const styleBackgroundColor = getStylePropertyUpdateValue(changes.style, 'backgroundColor', changes.breakpoint);
          const newBgColor = styleBackgroundColor || directStyles.backgroundColor;
          if (newBgColor) {
            if (!updateData.properties) {
              updateData.properties = { ...comp.properties };
            }
            updateData.properties.backgroundColor = newBgColor;
          }
        }

        // CRITICAL: When applying size changes, remove width/height/minHeight from style
        // Components should fill their wrapper, so size controls dimensions, not style
        // PHASE 5 FIX: Also remove minHeight when changing height - CSS minHeight overrides height
        if (changes.size) {
          // ROBUST FIX: When setting size dimensions, clear style dimensions through
          // the same schema-aware path used for style writes.
          const needsWidthCleanup = changes.size.width !== undefined &&
            (hasSchemaAwareStyleProperty(comp, 'width', { projectSchemaVersion: activeSchemaVersion }) ||
              hasSchemaAwareStyleProperty(comp, 'minWidth', { projectSchemaVersion: activeSchemaVersion }));
          const needsHeightCleanup = changes.size.height !== undefined &&
            (hasSchemaAwareStyleProperty(comp, 'height', { projectSchemaVersion: activeSchemaVersion }) ||
              hasSchemaAwareStyleProperty(comp, 'minHeight', { projectSchemaVersion: activeSchemaVersion }));

          if (needsWidthCleanup || needsHeightCleanup) {
            const dimensionCleanup: Record<string, unknown> = {};
            if (changes.size.width !== undefined) {
              dimensionCleanup.width = undefined;
              dimensionCleanup.minWidth = undefined;
            }
            if (changes.size.height !== undefined) {
              dimensionCleanup.height = undefined;
              dimensionCleanup.minHeight = undefined;
            }

            updateData.style = applySchemaAwareStyleUpdate(
              { style: updateData.style ?? comp.style },
              dimensionCleanup,
            {
              projectSchemaVersion: activeSchemaVersion,
              breakpoint: changes.breakpoint,
              styleWriteTarget: activeStyleWriteTarget,
            },
          );
          }
        }

        const positionUpdate = completePosition(changes.position, comp.position);
        if (positionUpdate) {
          updateData.position = positionUpdate;
        }

        if (responsiveUpdates.width === undefined) {
          const widthCandidate = toNumeric(
            getStylePropertyUpdateValue(changes.style, 'width', changes.breakpoint) !== undefined
              ? getStylePropertyUpdateValue(changes.style, 'width', changes.breakpoint)
              : directStyles.width
          );
          if (widthCandidate !== undefined) {
            responsiveUpdates.width = widthCandidate;
          }
        }

        if (responsiveUpdates.height === undefined) {
          const heightCandidate = toNumeric(
            getStylePropertyUpdateValue(changes.style, 'height', changes.breakpoint) !== undefined
              ? getStylePropertyUpdateValue(changes.style, 'height', changes.breakpoint)
              : directStyles.height
          );
          if (heightCandidate !== undefined) {
            responsiveUpdates.height = heightCandidate;
          }
        }

        if (updateData.position) {
          responsiveUpdates.position = updateData.position;
        } else if (changes.position) {
          responsiveUpdates.position = changes.position;
        }

        if (Object.keys(updateData).length > 0) {
          updateComponent(comp.id, updateData);
        }

        if (
          responsiveUpdates.width !== undefined ||
          responsiveUpdates.height !== undefined ||
          responsiveUpdates.position
        ) {
          syncResponsiveProperties(comp.id, responsiveUpdates);
        }
      });
    }

    // Mark message as applied
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, applied: true } : msg
    ));
  };

  return { applyPropertyChanges };
}
